import React, { useState, useEffect, useRef, useMemo } from "react";
import { Button, Badge, Text, SlideOver, Table } from "@teals/ui-web";
import type { Column } from "@teals/ui-web";
import type { DataSource, SchemaField, FilterConfig } from "../../store/api/dataSourcesApi";
import {
  useUpdateAgentAssociationsMutation,
  useUpdateDataSourceMutation,
  useInsertDataSourceRecordsMutation,
} from "../../store/api/dataSourcesApi";
import { useGetAgentsQuery } from "../../store/api/agentsApi";
import FilterConfigEditor from "./FilterConfigEditor";
import { emptyFilterConfig, toFilterConfigPayload } from "./filterConfig";
import {
  DS_TYPE_LABELS,
  DS_TYPE_BADGE_VARIANT,
  BYO_PROVIDER_LABELS,
} from "./constants";
import {
  DetailSection,
  DetailSectionTitle,
  DetailField,
  DetailFieldLabel,
  DetailFieldValue,
  AssociationItem,
  AssociationName,
  Divider,
  UploadZone,
  UploadZoneIcon,
  UploadZoneText,
  UploadZoneHint,
  TabBar,
  Tab,
} from "./styles";

type TabKey = "overview" | "upload";

interface DataSourceDetailProps {
  ds: DataSource | null;
  isOpen: boolean;
  onClose: () => void;
  initialTab?: TabKey;
  deleteConfirm?: string | null;
  onDeleteConfirm?: (id: string | null) => void;
  onDelete?: (id: string) => void;
}

const DataSourceDetail: React.FC<DataSourceDetailProps> = ({
  ds,
  isOpen,
  onClose,
  initialTab = "overview",
  deleteConfirm,
  onDeleteConfirm,
  onDelete,
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [schemaFields, setSchemaFields] = useState<SchemaField[]>([]);
  const [schemaDirty, setSchemaDirty] = useState(false);
  const [filterConfig, setFilterConfig] =
    useState<FilterConfig>(emptyFilterConfig);
  const [filtersDirty, setFiltersDirty] = useState(false);
  const [addAgentId, setAddAgentId] = useState("");
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: agents = [] } = useGetAgentsQuery();
  const [updateAssociations] = useUpdateAgentAssociationsMutation();
  const [updateDataSource, { isLoading: savingDs }] =
    useUpdateDataSourceMutation();
  const [insertRecords, { isLoading: uploading }] =
    useInsertDataSourceRecordsMutation();

  useEffect(() => {
    setActiveTab(initialTab);
    setSelectedFile(null);
    setUploadStatus(null);
    setSchemaDirty(false);
    setSchemaFields(ds?.schemaDefinition?.fields ?? []);
    setFilterConfig(ds?.filterConfig ?? emptyFilterConfig());
    setFiltersDirty(false);
    setAddAgentId("");
  }, [ds, initialTab]);

  const isByo = ds?.dsType === "byo";
  const providerLabel = ds?.provider
    ? (BYO_PROVIDER_LABELS[ds.provider] ?? ds.provider)
    : null;

  const recordLabel =
    ds && ds.recordCount != null && ds.recordCount > 0
      ? ds.recordCount.toLocaleString()
      : "0";

  const associatedAgents = useMemo(() => {
    const ids = new Set(ds?.agentIds || []);
    return agents.filter((a) => ids.has(a.id) || ids.has(a.agentName));
  }, [ds?.agentIds, agents]);

  const availableAgents = useMemo(() => {
    const ids = new Set(ds?.agentIds || []);
    return agents.filter(
      (a) => !a.readOnly && !ids.has(a.id) && !ids.has(a.agentName),
    );
  }, [ds?.agentIds, agents]);

  const schemaColumns: Column<SchemaField>[] = useMemo(
    () => [
      { key: "name", header: "Field Name" },
      { key: "type", header: "Type" },
      {
        key: "searchable",
        header: "Searchable",
        align: "center",
        render: (_val: boolean, row: SchemaField) => (
          <input
            type="checkbox"
            checked={row.searchable}
            onChange={() => {
              setSchemaFields((prev) =>
                prev.map((f) =>
                  f.name === row.name
                    ? { ...f, searchable: !f.searchable }
                    : f,
                ),
              );
              setSchemaDirty(true);
            }}
          />
        ),
      },
      {
        key: "indexable",
        header: "Indexable",
        align: "center",
        render: (_val: boolean, row: SchemaField) => (
          <input
            type="checkbox"
            checked={row.indexable}
            onChange={() => {
              setSchemaFields((prev) =>
                prev.map((f) =>
                  f.name === row.name ? { ...f, indexable: !f.indexable } : f,
                ),
              );
              setSchemaDirty(true);
            }}
          />
        ),
      },
    ],
    [],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setUploadStatus(null);
  };

  const handleZoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleSaveSchema = async () => {
    if (!ds) return;
    if (
      !window.confirm(
        "Saving schema changes may affect agents that query this data source. Continue?",
      )
    ) {
      return;
    }
    await updateDataSource({
      id: ds.dataSourceId,
      body: { schema_definition: { fields: schemaFields } },
    }).unwrap();
    setSchemaDirty(false);
  };

  const handleSaveFilters = async () => {
    if (!ds) return;
    await updateDataSource({
      id: ds.dataSourceId,
      body: { filter_config: toFilterConfigPayload(filterConfig) ?? null },
    }).unwrap();
    setFiltersDirty(false);
  };

  const handleAddAgent = async () => {
    if (!ds || !addAgentId) return;
    const next = [...(ds.agentIds || []), addAgentId];
    await updateAssociations({ id: ds.dataSourceId, agentIds: next }).unwrap();
    setAddAgentId("");
  };

  const handleRemoveAgent = async (agentId: string) => {
    if (!ds) return;
    const next = (ds.agentIds || []).filter((id) => id !== agentId);
    await updateAssociations({ id: ds.dataSourceId, agentIds: next }).unwrap();
  };

  const handleUpload = async () => {
    if (!ds || !selectedFile) return;
    setUploadStatus(null);
    try {
      const text = await selectedFile.text();
      let records: Record<string, unknown>[];
      if (selectedFile.name.endsWith(".csv")) {
        const lines = text.trim().split(/\r?\n/);
        if (lines.length < 2) {
          setUploadStatus("CSV must include a header row and at least one record.");
          return;
        }
        const headers = lines[0].split(",").map((h) => h.trim());
        records = lines.slice(1).map((line) => {
          const cols = line.split(",");
          const row: Record<string, unknown> = {};
          headers.forEach((h, i) => {
            row[h] = (cols[i] || "").trim();
          });
          return row;
        });
      } else {
        const parsed = JSON.parse(text);
        records = Array.isArray(parsed) ? parsed : [parsed];
      }
      const result = await insertRecords({
        id: ds.dataSourceId,
        records,
      }).unwrap();
      setUploadStatus(
        `Uploaded ${result.insertedCount} records (total ${result.recordCount}).`,
      );
      setSelectedFile(null);
    } catch (err) {
      setUploadStatus(
        err instanceof Error ? err.message : "Upload failed. Try again.",
      );
    }
  };

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={ds?.name ?? ""}
      width="lg"
      position="right"
    >
      {ds && (
        <>
          <TabBar>
            <Tab
              $active={activeTab === "overview"}
              onClick={() => setActiveTab("overview")}
            >
              Overview
            </Tab>
            {!isByo && (
              <Tab
                $active={activeTab === "upload"}
                onClick={() => setActiveTab("upload")}
              >
                Upload Data
              </Tab>
            )}
          </TabBar>

          {activeTab === "overview" && (
            <>
              <DetailSection>
                <DetailSectionTitle>Data Source Info</DetailSectionTitle>
                <DetailField>
                  <DetailFieldLabel>Name</DetailFieldLabel>
                  <DetailFieldValue>{ds.name}</DetailFieldValue>
                </DetailField>
                <DetailField>
                  <DetailFieldLabel>Type</DetailFieldLabel>
                  <DetailFieldValue>
                    <Badge
                      variant={DS_TYPE_BADGE_VARIANT[ds.dsType] as any}
                      size="sm"
                    >
                      {DS_TYPE_LABELS[ds.dsType]}
                    </Badge>
                    {isByo && providerLabel && (
                      <Badge
                        variant="neutral"
                        size="sm"
                        style={{ marginLeft: 6 }}
                      >
                        {providerLabel}
                      </Badge>
                    )}
                  </DetailFieldValue>
                </DetailField>
                <DetailField>
                  <DetailFieldLabel>Data Source ID</DetailFieldLabel>
                  <DetailFieldValue style={{ fontFamily: "monospace" }}>
                    {ds.dataSourceId}
                  </DetailFieldValue>
                </DetailField>
                <DetailField>
                  <DetailFieldLabel>Records</DetailFieldLabel>
                  <DetailFieldValue>{recordLabel}</DetailFieldValue>
                </DetailField>
                <DetailField>
                  <DetailFieldLabel>Status</DetailFieldLabel>
                  <DetailFieldValue>
                    <Badge
                      variant={ds.status === "active" ? "success" : "warning"}
                      size="sm"
                    >
                      {ds.status}
                    </Badge>
                  </DetailFieldValue>
                </DetailField>
              </DetailSection>

              <DetailSection>
                <DetailSectionTitle>Schema</DetailSectionTitle>
                {schemaFields.length ? (
                  <>
                    <Table
                      columns={schemaColumns}
                      data={schemaFields}
                      rowKey="name"
                    />
                    {schemaDirty && (
                      <Button
                        variant="primary"
                        size="sm"
                        style={{ marginTop: 12 }}
                        onClick={handleSaveSchema}
                        disabled={savingDs}
                      >
                        {savingDs ? "Saving…" : "Save schema"}
                      </Button>
                    )}
                  </>
                ) : (
                  <Text
                    variant="body1"
                    style={{
                      color: "var(--text-muted)",
                      fontSize: "0.8125rem",
                    }}
                  >
                    No schema fields defined
                  </Text>
                )}
              </DetailSection>

              <DetailSection>
                <DetailSectionTitle>Query filters</DetailSectionTitle>
                <FilterConfigEditor
                  value={filterConfig}
                  onChange={(next) => {
                    setFilterConfig(next);
                    setFiltersDirty(true);
                  }}
                  fieldOptions={schemaFields.map((f) => f.name)}
                />
                {filtersDirty && (
                  <Button
                    variant="primary"
                    size="sm"
                    style={{ marginTop: 12 }}
                    onClick={handleSaveFilters}
                    disabled={savingDs}
                  >
                    {savingDs ? "Saving…" : "Save filters"}
                  </Button>
                )}
              </DetailSection>

              <DetailSection>
                <DetailSectionTitle>Agent Associations</DetailSectionTitle>
                {associatedAgents.length > 0 ? (
                  associatedAgents.map((agent) => (
                    <AssociationItem key={agent.id}>
                      <AssociationName>{agent.name}</AssociationName>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveAgent(agent.id)}
                      >
                        Remove
                      </Button>
                    </AssociationItem>
                  ))
                ) : (
                  <Text
                    variant="body1"
                    style={{
                      color: "var(--text-muted)",
                      fontSize: "0.8125rem",
                    }}
                  >
                    No agents associated
                  </Text>
                )}
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    marginTop: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <select
                    value={addAgentId}
                    onChange={(e) => setAddAgentId(e.target.value)}
                    style={{
                      padding: "0.4rem 0.5rem",
                      borderRadius: 8,
                      border: "1px solid var(--border)",
                      background: "var(--bg-surface)",
                      color: "var(--text-primary)",
                    }}
                  >
                    <option value="">Select agent…</option>
                    {availableAgents.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleAddAgent}
                    disabled={!addAgentId}
                  >
                    + Add Agent
                  </Button>
                </div>
              </DetailSection>

              <DetailSection>
                <DetailSectionTitle>Organisation</DetailSectionTitle>
                <AssociationItem>
                  <AssociationName>{ds.orgName}</AssociationName>
                  <Badge variant="success" size="sm">
                    Active
                  </Badge>
                </AssociationItem>
              </DetailSection>

              <Divider />

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                {deleteConfirm === ds.dataSourceId ? (
                  <>
                    <Text
                      variant="body1"
                      style={{
                        color: "var(--danger, #dc2626)",
                        marginRight: 12,
                        alignSelf: "center",
                      }}
                    >
                      Are you sure?
                    </Text>
                    <Button
                      variant="ghost"
                      onClick={() => onDeleteConfirm?.(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => onDelete?.(ds.dataSourceId)}
                      style={{
                        background: "var(--danger, #dc2626)",
                        marginLeft: 8,
                      }}
                    >
                      Delete
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="ghost"
                    onClick={() => onDeleteConfirm?.(ds.dataSourceId)}
                    style={{ color: "var(--danger, #dc2626)" }}
                  >
                    Delete Data Source
                  </Button>
                )}
              </div>
            </>
          )}

          {activeTab === "upload" && !isByo && (
            <>
              <Text
                variant="body1"
                style={{ marginBottom: 16, color: "var(--text-secondary)" }}
              >
                Manually upload records to <strong>{ds.name}</strong>. For
                production ingestion, use the Direct API with your API Key
                Profile credentials.
              </Text>

              <UploadZone onClick={handleZoneClick}>
                <UploadZoneIcon>{"\uD83D\uDCC2"}</UploadZoneIcon>
                <UploadZoneText>
                  Drop JSON or CSV files here, or click to browse
                </UploadZoneText>
                <UploadZoneHint>Max 10 MB per file</UploadZoneHint>
              </UploadZone>

              <input
                ref={fileInputRef}
                type="file"
                accept=".json,.csv"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />

              {selectedFile && (
                <div style={{ marginTop: 12 }}>
                  <Text
                    variant="body1"
                    style={{
                      fontSize: "0.875rem",
                      color: "var(--text-primary)",
                    }}
                  >
                    {selectedFile.name} (
                    {(selectedFile.size / 1024).toFixed(1)} KB)
                  </Text>
                  <Button
                    variant="primary"
                    size="sm"
                    style={{ marginTop: 8 }}
                    onClick={handleUpload}
                    disabled={uploading}
                  >
                    {uploading ? "Uploading…" : "Upload"}
                  </Button>
                </div>
              )}

              {uploadStatus && (
                <Text
                  variant="body1"
                  style={{
                    marginTop: 12,
                    fontSize: "0.875rem",
                    color: "var(--text-secondary)",
                  }}
                >
                  {uploadStatus}
                </Text>
              )}
            </>
          )}
        </>
      )}
    </SlideOver>
  );
};

export default DataSourceDetail;
